import type { AlgorithmChallenge } from '@code-arcade/engine'

export const bubbleSort: AlgorithmChallenge = {
  id: 'bubble-sort',
  title: 'Bubble Sort',
  level: 1,
  chapter: 'chapter-1',
  mode: 'race-it',
  description: 'The O(n²) algorithm that exists to teach you why it matters.',
  realWorld: {
    scenario: 'Sorting a 50-row admin table client-side at Innotek Systems Inc.',
    consequence: 'Fine at 50 rows. A production incident at 500,000.',
    products: [],
  },
  visualization: {
    type: 'bar-array',
    defaultSize: 20,
    allowCustomInput: false,
  },
  interrupts: [
    {
      atStep: 10,
      character: 'stacy-queue',
      mood: 'urgent',
      message: "Can you make the table sort faster? It feels slow.",
    },
  ],
  complexity: { time: 'O(n²)', space: 'O(1)' },
  xpReward: { oneStar: 50, twoStar: 100, threeStar: 200 },

  keyTakeaway:
    'Every nested loop over n items that also scans n items is bubble sort with different variable names. ' +
    'The admin table sort that took 10ms at 50 rows will take 10 minutes at 500,000 — not because the code changed, ' +
    'but because O(n²) always does this. The real skill bubble sort builds is pattern recognition: spotting the ' +
    '`for row in users: for tag in row.tags` before it ships. You will catch this in code review. ' +
    'You will catch it in your own code. That is worth more than any sort implementation.',

  whenToUse: [
    'Teaching comparison-based sorting — nothing is clearer to visualize',
    'Arrays under ~20 elements where simplicity beats performance',
    'Recognizing O(n²) patterns in code review — nested loops over data are bubble sort in disguise',
    'Early-exit optimization: add a `swapped` flag and you get O(n) on already-sorted input — the same principle applies to any loop that can stop early when work is done',
    'Embedded systems with tiny, fixed-size datasets and no standard library',
  ],

  whenNotToUse: [
    'Any production data pipeline — use your language\'s built-in sort',
    'Arrays over a few hundred elements (you will feel it)',
    'Anywhere latency matters to an actual user',
  ],

  pseudocode: `procedure bubbleSort(A)
  n ← length(A)
  for i from 0 to n - 2
    for j from 0 to n - 2 - i
      if A[j] > A[j + 1]
        swap A[j] and A[j + 1]
  return A

// Optimised: exit early if no swaps in a full pass
procedure bubbleSortOptimised(A)
  n ← length(A)
  repeat
    swapped ← false
    for j from 0 to n - 2
      if A[j] > A[j + 1]
        swap A[j] and A[j + 1]
        swapped ← true
    n ← n - 1
  until not swapped`,

  sampleDataset: {
    label: 'Admin table — employee IDs (Innotek Systems Inc.)',
    preview: [1042, 2891, 3, 456, 1205, 788, 2034, 91, 1567, 342, 2750, 619],
    atScale: {
      n: 500000,
      leftLabel: 'Bubble Sort',
      leftNote: '~ 10 min',
      rightLabel: 'Array.sort()',
      rightNote: '< 50 ms',
    },
  },

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static void BubbleSort(int[] arr) {
    int n = arr.Length;
    for (int i = 0; i < n - 1; i++) {
        for (int j = 0; j < n - 1 - i; j++) {
            if (arr[j] > arr[j + 1]) {
                (arr[j], arr[j + 1]) = (arr[j + 1], arr[j]);
            }
        }
    }
}

// Array.sort() replaced this — now you know exactly why O(n log n) matters:
Array.Sort(arr);               // introspective sort O(n log n)
list.Sort();                   // List<T> — same internals`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def bubble_sort(arr: list[int]) -> list[int]:
    n = len(arr)
    for i in range(n - 1):
        for j in range(n - 1 - i):
            if arr[j] > arr[j + 1]:
                arr[j], arr[j + 1] = arr[j + 1], arr[j]
    return arr


# Array.sort() replaced this — now you know exactly why O(n log n) matters:
arr.sort()                # in-place  O(n log n) TimSort
sorted_arr = sorted(arr)  # new list  O(n log n) TimSort`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function bubbleSort(arr) {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - 1 - i; j++) {
      if (arr[j] > arr[j + 1]) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
      }
    }
  }
  return arr;
}

// Array.sort() replaced this — now you know exactly why O(n log n) matters:
arr.sort((a, b) => a - b); // V8 TimSort, O(n log n)`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function bubbleSort(arr: number[]): number[] {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - 1 - i; j++) {
      if (arr[j] > arr[j + 1]) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
      }
    }
  }
  return arr;
}

// Array.sort() replaced this — now you know exactly why O(n log n) matters:
arr.sort((a, b) => a - b); // V8 TimSort, O(n log n)`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static void bubbleSort(int[] arr) {
    int n = arr.length;
    for (int i = 0; i < n - 1; i++) {
        for (int j = 0; j < n - 1 - i; j++) {
            if (arr[j] > arr[j + 1]) {
                int temp = arr[j];
                arr[j] = arr[j + 1];
                arr[j + 1] = temp;
            }
        }
    }
}

// Array.sort() replaced this — now you know exactly why O(n log n) matters:
Arrays.sort(arr);              // dual-pivot quicksort O(n log n)
Arrays.sort(objArr, comparator); // TimSort for objects`,
    },
  ],
}
